// Invoice payment service — payment-hold enforcement for RECEIVED invoices.
// Uses InvoiceDao for data access. No raw Supabase calls.
//
// A received invoice is HELD (payment_status='on_hold') until ALL applicable
// release conditions are met. Conditions are evaluated in order:
//   1. Approved        — every invoice: review_status='approved'.
//   2. Transport docs  — transport invoices: each linked tx has its required
//                        approved original (annex7 / CMR per transaction_rules).
//   3. Card            — payment_method='card': held until paid (DB trigger flips
//                        on_hold→paid automatically; we keep it on_hold here).
//   4. Verified account — seller_bank_account must belong to the vendor's payable
//                        tier (payment_verified / operator_verified). Only enforced
//                        when the gate data is supplied; no-account invoices skip it.
//   5. else            — RELEASE: on_hold → unpaid, clear hold_reason.

import { InvoiceDao, type ReceivedHoldCheckRow } from './InvoiceDao.ts';
import {
  ANNEX_HOLD_REASON, CMR_HOLD_REASON, NOT_APPROVED_HOLD_REASON, CASH_OR_CARD_HOLD_REASON,
  UNVERIFIED_ACCOUNT_HOLD_REASON, MANAGED_HOLD_REASONS, isCashOrCardPayment,
} from './holdReasons.ts';
import { ibanCore, canonicalizeIbans } from '../../utils/iban.ts';

// Re-export so existing importers (tests, callers) keep resolving these from here.
export {
  ANNEX_HOLD_REASON, CMR_HOLD_REASON, NOT_APPROVED_HOLD_REASON, CASH_OR_CARD_HOLD_REASON,
  UNVERIFIED_ACCOUNT_HOLD_REASON,
};

export interface HoldResult {
  held: number;
  cleared: number;
}

export interface HoldDecision {
  hold: boolean;
  reason: string | null;
}

export class InvoicePaymentService {
  /** Apply the ordered hold policy to a single received invoice. */
  static readonly evaluateHold = evaluateHoldImpl;

  constructor(private readonly dao: InvoiceDao) {}

  /**
   * Evaluate the payment-hold policy across received invoices.
   * Sets on_hold (+reason) when a release condition is unmet; clears managed
   * holds (on_hold → unpaid) once all conditions are met.
   */
  async enforceInvoiceHolds(invoiceId?: number): Promise<HoldResult> {
    console.log(`[payment-holds] starting enforcement check${invoiceId ? ` for invoice ${invoiceId}` : ' (all)'}`);

    // Query 1: received invoices + (left-joined) links + transports + tx + rules
    const invoices = await this.dao.getReceivedInvoicesForHoldCheck(invoiceId);
    console.log(`[payment-holds] found ${invoices.length} received invoices to check`);
    if (invoices.length === 0) return { held: 0, cleared: 0 };

    // Collect all transaction IDs to check files for (transport invoices only)
    const txIds = new Set<number>();
    for (const inv of invoices) {
      for (const link of inv.invoice_links ?? []) {
        if (link.stale) continue;
        const txId = link.transaction_transports?.transactions?.id;
        if (txId != null) txIds.add(txId);
      }
    }
    console.log(`[payment-holds] checking ${txIds.size} unique transactions for documents`);

    // Query 2: approved original docs
    const docs = await this.dao.getApprovedOriginalDocs([...txIds]);
    const approvedDocs = new Set(docs.map(d => `${d.transaction_id}:${d.document_type}`));
    console.log(`[payment-holds] found ${docs.length} approved original docs`, {
      docs: docs.map(d => `tx${d.transaction_id}:${d.document_type}`),
    });

    // Query 3: payable (verified) account cores per vendor — only for vendors whose
    // invoices actually carry a seller_bank_account (condition 4 skips no-account
    // invoices anyway, so there's nothing to verify them against).
    const companyIdsWithAccount = [...new Set(
      invoices.filter((i) => i.seller_bank_account).map((i) => i.seller_company_id),
    )];
    const verifiedAccounts = await this.dao.getVerifiedAccountCores(companyIdsWithAccount);

    // Determine holds
    const toHold = new Map<number, string>(); // invoiceId → reason
    // Level-triggered: every invoice that SHOULD be on hold, not just the ones
    // transitioning into it (see the marker note below).
    const shouldBeMarked: number[] = [];
    const toClearUnpaid: number[] = [];  // release with no payment received
    const toClearPartial: number[] = []; // release but a bank payment already landed

    for (const inv of invoices) {
      const decision = InvoicePaymentService.evaluateHold(inv, approvedDocs, verifiedAccounts);

      if (decision.hold) {
        if (decision.reason == null) {
          // Invariant guard: every hold path must carry a managed reason, else the
          // invoice could never be auto-released. Skip rather than write a null hold.
          console.log(`[payment-holds] invoice ${inv.id}: hold with no reason — skipping (bug guard)`);
          continue;
        }
        // Never touch a MANUAL / external hold — one whose reason we don't manage.
        // Overwriting it (then later auto-clearing a managed reason) would bypass a
        // compliance/legal hold an operator placed.
        const isManualHold = inv.payment_status === 'on_hold'
          && (inv.hold_reason == null || !MANAGED_HOLD_REASONS.has(inv.hold_reason));
        if (isManualHold) {
          console.log(`[payment-holds] invoice ${inv.id}: manual hold (${inv.hold_reason}) — leaving untouched`);
          continue;
        }
        const reason = decision.reason;
        if (inv.payment_status !== 'on_hold' || inv.hold_reason !== reason) {
          console.log(`[payment-holds] invoice ${inv.id}: HOLD — ${reason} (was ${inv.payment_status}/${inv.hold_reason})`);
          toHold.set(inv.id, reason);
        } else {
          console.log(`[payment-holds] invoice ${inv.id}: already on hold — ${inv.hold_reason}`);
        }
        // The hold DECISION is edge-triggered (only transitions reach toHold), but
        // the marker is LEVEL-triggered: every invoice that should be on hold must
        // carry it. Without this the 518 invoices already sitting on_hold would
        // never be marked — they never transition again, so they would never
        // qualify. syncHoldMarkers writes only rows whose note actually changes,
        // so a steady-state sweep still writes nothing.
        shouldBeMarked.push(inv.id);
      } else {
        // Release only our own managed holds. Restore the correct payment state: a
        // held invoice can have received a partial bank payment (the trigger keeps it
        // on_hold but records paid_amount) — releasing to 'unpaid' would lose that and
        // re-pay the full gross. partial iff paid_amount > 0, else unpaid.
        if (inv.payment_status === 'on_hold' && inv.hold_reason != null && MANAGED_HOLD_REASONS.has(inv.hold_reason)) {
          if ((inv.paid_amount ?? 0) > 0) {
            console.log(`[payment-holds] invoice ${inv.id}: CLEAR → partial (paid_amount=${inv.paid_amount}, was: ${inv.hold_reason})`);
            toClearPartial.push(inv.id);
          } else {
            console.log(`[payment-holds] invoice ${inv.id}: CLEAR → unpaid (was: ${inv.hold_reason})`);
            toClearUnpaid.push(inv.id);
          }
        } else {
          console.log(`[payment-holds] invoice ${inv.id}: OK — no managed hold to apply`);
        }
      }
    }

    // Write holds grouped by reason
    const byReason = new Map<string, number[]>();
    for (const [id, reason] of toHold) {
      const bucket = byReason.get(reason);
      if (bucket) bucket.push(id);
      else byReason.set(reason, [id]);
    }
    for (const [reason, ids] of byReason) {
      console.log(`[payment-holds] setting hold on ${ids.length} invoice(s): ${reason}`);
      await this.dao.updateStatusBulk(ids, { payment_status: 'on_hold', hold_reason: reason }, { invalidate: false });
    }

    // Clear holds — restore the correct payment state per invoice.
    if (toClearUnpaid.length > 0) {
      console.log(`[payment-holds] clearing hold → unpaid on ${toClearUnpaid.length} invoice(s)`);
      await this.dao.updateStatusBulk(toClearUnpaid, { payment_status: 'unpaid', hold_reason: null }, { invalidate: false });
    }
    if (toClearPartial.length > 0) {
      console.log(`[payment-holds] clearing hold → partial on ${toClearPartial.length} invoice(s)`);
      await this.dao.updateStatusBulk(toClearPartial, { payment_status: 'partial', hold_reason: null }, { invalidate: false });
    }

    // Bring the PENDING marker in line with the decisions just written. The bulk
    // status writes above cannot do it — the marker is a per-row edit of existing
    // note text, and updateStatusBulk carries neither `notes` nor
    // fakturownia_dirty. Without this an automatically-applied hold never reached
    // Fakturownia: 519 prod invoices on_hold, none marked locally.
    const markersWritten = await this.dao.syncHoldMarkers(
      shouldBeMarked,
      [...toClearUnpaid, ...toClearPartial],
    );

    const cleared = toClearUnpaid.length + toClearPartial.length;
    console.log(`[payment-holds] done — held: ${toHold.size}, cleared: ${cleared}, markers: ${markersWritten}`);
    return { held: toHold.size, cleared };
  }
}

/** Does this invoice have at least one non-stale link to a transport? */
function isTransportInvoice(inv: ReceivedHoldCheckRow): boolean {
  return (inv.invoice_links ?? []).some(l => !l.stale && l.transaction_transports != null);
}

/**
 * Apply the ordered hold policy to a single received invoice.
 * Returns whether to hold and (if so) the reason.
 */
function evaluateHoldImpl(
  inv: ReceivedHoldCheckRow,
  approvedDocs: Set<string>,
  verifiedAccounts?: Map<number, Set<string>>,
): HoldDecision {
  // 1. Approved (every invoice)
  if (inv.review_status !== 'approved') {
    return { hold: true, reason: NOT_APPROVED_HOLD_REASON };
  }

  // 2. Transport docs (transport invoices only)
  if (isTransportInvoice(inv)) {
    const missing = findMissingDocument(inv, approvedDocs);
    if (missing) return { hold: true, reason: missing };
  }

  // 3. Cash or card — held until paid (paid invoices are filtered out before this point)
  if (isCashOrCardPayment(inv.payment_method)) {
    return { hold: true, reason: CASH_OR_CARD_HOLD_REASON };
  }

  // 4. Verified seller account — only when gate data is supplied AND the invoice
  //    carries an account. No-account invoices are NOT held by this rule: they are
  //    already unpayable via the outbound no-IBAN skip, and holding them would only
  //    hide the missing-IBAN problem. `seller_bank_account` can hold a multi-account
  //    blob (several IBANs joined by "<BR>"/newlines/etc — see canonicalizeIbans);
  //    parse it into its constituent cores and match by ibanCore, currency-agnostic,
  //    passing when ANY parsed core is payable. An empty parse (garbage/no account)
  //    matches nothing, same as the prior single-value unmatched case.
  if (verifiedAccounts && inv.seller_bank_account) {
    const cores = verifiedAccounts.get(inv.seller_company_id);
    const parsedIbans = canonicalizeIbans(inv.seller_bank_account);
    const matches = cores != null && [...parsedIbans].some((iban) => cores.has(ibanCore(iban)));
    if (!matches) {
      return { hold: true, reason: UNVERIFIED_ACCOUNT_HOLD_REASON };
    }
  }

  // 5. Release
  return { hold: false, reason: null };
}

/** Check if any linked transport is missing a required document. Returns hold reason or null. */
function findMissingDocument(inv: ReceivedHoldCheckRow, approvedDocs: Set<string>): string | null {
  for (const link of inv.invoice_links ?? []) {
    if (link.stale) continue;
    const tp = link.transaction_transports;
    if (!tp) { console.log(`[payment-holds] invoice ${inv.id}: link has no transport data — skipping`); continue; }
    const tx = tp.transactions;
    if (!tx) { console.log(`[payment-holds] invoice ${inv.id}: transport ${tp.id} has no transaction data — skipping`); continue; }
    const rule = tx.transaction_rules;
    if (!rule) { console.log(`[payment-holds] invoice ${inv.id}: tx ${tx.id} has no rule — skipping`); continue; }

    console.log(`[payment-holds] invoice ${inv.id}: tx ${tx.id} — annex_original_approved_required=${rule.annex_original_approved_required}, cmr_original_approved_required=${rule.cmr_original_approved_required}, has_annex=${approvedDocs.has(`${tx.id}:annex7`)}, has_cmr=${approvedDocs.has(`${tx.id}:cmr`)}`);

    if (rule.annex_original_approved_required && !approvedDocs.has(`${tx.id}:annex7`)) {
      return ANNEX_HOLD_REASON;
    }
    if (rule.cmr_original_approved_required && !approvedDocs.has(`${tx.id}:cmr`)) {
      return CMR_HOLD_REASON;
    }
  }
  return null;
}

// Existing importers (tests, edge fns) keep resolving this from the module; the
// implementation now lives on the class per the class-based Service/DAO rule.
export const evaluateHold = InvoicePaymentService.evaluateHold;
